import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/apiConfig';

/** RFQ sepeti tarayıcıda; gönderimde önce POST /api/rfq/submit; 502 / ağ hatasında mailto yedeği */

const RFQ_CONTEXT = createContext();

function fallbackRfqRecipient() {
  return (process.env.REACT_APP_RFQ_MAILTO_EMAIL || '').trim().replace(/^mailto:/i, '');
}

/** API çökmüş veya nginx 502 ise RFQ içeriği bu adrese gidiyormuş gibi mail uygulaması açılır */
function buildRfqFallbackMailto(payload, basketLines) {
  const recipient = fallbackRfqRecipient();
  if (!recipient) return null;
  const rawSub = `[DeMaxtore RFQ — Yedek] ${payload.company_name || 'Lead'}`.replace(/\s+/g, ' ').trim().slice(0, 118);
  const subject = encodeURIComponent(rawSub);

  const lines = [
    '[Sunucuya ulaşılamadığı için gönderilen yedek RFQ]',
    '',
    `Company: ${payload.company_name || ''}`,
    `Business email (form): ${payload.contact_email || ''}`,
    `Phone: ${payload.contact_phone || ''}`,
    `Contact person: ${payload.contact_name || ''}`,
    '',
    `Departure: ${payload.departure_port || ''}`,
    `Destination: ${payload.destination_point || ''}`,
    `Delivery location: ${payload.delivery_location || ''}`,
    `Product/service: ${payload.product_service || ''}`,
    `Quantity (text): ${payload.quantity_text || ''}`,
    `Supplier types: ${payload.supplier_types || ''}`,
    '',
    'Request details:',
    payload.request_details || '',
    '',
    'Basket:',
  ];

  basketLines.forEach((it) => {
    lines.push(`- ${it.product_name} (ID: ${it.product_id}) — ${formatLineSpec(it)}`);
  });

  const body = encodeURIComponent(lines.join('\n').slice(0, 7900));

  let href = `mailto:${recipient}?subject=${subject}&body=${body}`;
  if (href.length > 20000) {
    const shortLines = [...lines.slice(0, -basketLines.length - 3), '', 'Basket: (liste kısaltıldı)'];
    href = `mailto:${recipient}?subject=${subject}&body=${encodeURIComponent(shortLines.join('\n').slice(0, 4000))}`;
  }

  return href;
}

/** API yanıtı yok / bad gateway ise mail ile devam etmeyi güvenilir kılar */
function shouldMailtoFallbackSubmitError(error) {
  if (!axios.isAxiosError(error)) return false;
  if (error.response?.status === 429) return false;

  const st = error.response?.status;
  const code = error.code || '';

  if (code === 'ERR_NETWORK' || code === 'ECONNABORTED' || code === 'ETIMEDOUT') return true;
  if (error.message === 'Network Error' || st === undefined) return true;
  if ([502, 503, 504, 404, 408, 413].includes(st)) return true;

  return false;
}

const LOCAL_BASKET_KEY = 'demax_rfq_local_basket_v1';
const LEGACY_FALLBACK_KEY = 'demax_rfqlocal_lines_v2';
const RFQ_SESSION_LEGACY_KEY = 'rfq_session_id';

function mintLineId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `rfq_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}



function parseQtyFromText(text) {
  const t = String(text || '').trim();
  if (!t) return 1;
  const m = t.match(/^\s*(\d+(?:[.,]\d+)?)/) || t.match(/(\d+(?:[.,]\d+)?)/);
  if (m) {
    const v = parseFloat(m[1].replace(',', '.'));
    if (Number.isFinite(v) && v > 0) return Math.max(1, Math.min(999999, Math.floor(v)));
  }
  return 1;
}

function parseUnitType(value) {
  const v = String(value || '').trim().toLowerCase();
  if (v === 'ton' || v === 'container' || v === 'carton') return v;
  return 'carton';
}

function parseUnitQty(value) {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : Number(value);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.max(1, Math.min(999999, Math.floor(n)));
}

function sanitizeUnitPrice(value) {
  return String(value ?? '').trim().slice(0, 64);
}

function formatLineSpec(line) {
  const unitType = parseUnitType(line?.unit_type);
  const unitQty = line?.unit_qty != null ? parseUnitQty(line.unit_qty) : parseUnitQty(line?.containers);
  const unitPrice = sanitizeUnitPrice(line?.unit_price);
  const unitLabel =
    unitType === 'ton' ? 'ton' : unitType === 'container' ? 'container' : 'carton';
  const plural = unitQty === 1 ? unitLabel : `${unitLabel}s`;
  const spec = `${unitQty} ${plural}`;
  return unitPrice ? `${spec} @ ${unitPrice}` : spec;
}

/** @deprecated legacy container count — kept for older saved baskets */
function parseContainers(value) {
  return parseUnitQty(value);
}

/** Workspace embed: sepet gizliyken form alanlarından satır üretir. */
function buildFormOnlyBasketLines(payload) {
  const formProducts = Array.isArray(payload?.form_products) ? payload.form_products : null;
  const noteParts = [];
  if (payload?.request_details) noteParts.push(String(payload.request_details).trim());
  if (payload?.delivery_location) noteParts.push(`Delivery: ${String(payload.delivery_location).trim()}`);
  const sharedNote = noteParts.join('\n').slice(0, 2000);

  if (formProducts && formProducts.length) {
    const lines = [];
    for (const row of formProducts) {
      const productName = String(row?.name ?? row?.product_name ?? '').trim();
      if (!productName) continue;
      const line = sanitizeLine({
        id: mintLineId(),
        product_id: `form-${mintLineId().replace(/-/g, '').slice(0, 16)}`,
        product_name: productName,
        quantity: parseUnitQty(row?.unit_qty ?? row?.containers),
        unit_type: parseUnitType(row?.unit_type),
        unit_qty: parseUnitQty(row?.unit_qty ?? row?.containers),
        unit_price: sanitizeUnitPrice(row?.unit_price),
        note: sharedNote,
        created_at: new Date().toISOString(),
      });
      if (line) lines.push(line);
    }
    return lines;
  }

  const productName = String(payload?.product_service || '').trim();
  if (!productName) return [];
  const qty = parseQtyFromText(payload?.quantity_text);
  const line = sanitizeLine({
    id: mintLineId(),
    product_id: `form-${mintLineId().replace(/-/g, '').slice(0, 16)}`,
    product_name: productName,
    quantity: qty,
    unit_type: 'carton',
    unit_qty: qty,
    unit_price: '',
    note: sharedNote,
    created_at: new Date().toISOString(),
  });
  return line ? [line] : [];
}
function sanitizeLine(row) {
  if (!row || typeof row !== 'object') return null;
  const product_id = String(row.product_id ?? '').trim().slice(0, 128);
  const product_name = String(row.product_name ?? '').trim().slice(0, 500);
  if (!product_id || !product_name) return null;
  const qty = typeof row.quantity === 'number' && Number.isFinite(row.quantity) ? row.quantity : Number(row.quantity) || 1;
  const unit_type = parseUnitType(row.unit_type);
  const unit_qty =
    row.unit_qty != null && row.unit_qty !== ''
      ? parseUnitQty(row.unit_qty)
      : row.containers != null && row.containers !== ''
        ? parseUnitQty(row.containers)
        : parseUnitQty(qty);
  const unit_price = sanitizeUnitPrice(row.unit_price);
  return {
    id: row.id ? String(row.id) : mintLineId(),
    product_id,
    product_name,
    product_name_tr: String(row.product_name_tr || '').trim().slice(0, 500),
    quantity: Math.max(1, Math.min(999999, Math.floor(qty))),
    unit_type,
    unit_qty,
    unit_price,
    containers: unit_type === 'container' ? Math.min(9999, unit_qty) : parseUnitQty(row.containers || 1),
    note: String(row.note || '').slice(0, 2000),
    product_image: String(row.product_image || '').trim().slice(0, 2048),
    created_at: typeof row.created_at === 'string' ? row.created_at : new Date().toISOString(),
  };
}

function readBasketFromDisk() {
  try {
    const raw = localStorage.getItem(LOCAL_BASKET_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const arr = Array.isArray(parsed) ? parsed : Array.isArray(parsed?.items) ? parsed.items : [];
      const cleaned = [];
      for (const r of arr) {
        const s = sanitizeLine(r);
        if (s) cleaned.push(s);
      }
      if (cleaned.length > 0) return cleaned;
    }
  } catch {
    /* empty */
  }

  /** Eskiden API bozukluğundan kalan tek satır yedeği → bir kerelik taşı */
  try {
    const leg = localStorage.getItem(LEGACY_FALLBACK_KEY);
    if (leg) {
      const parsed = JSON.parse(leg);
      const arr = Array.isArray(parsed) ? parsed : [];
      const cleaned = [];
      for (const r of arr) {
        const s = sanitizeLine({
          ...r,
          quantity: r.quantity ?? 1,
          __local_fallback: undefined,
        });
        if (s) cleaned.push(s);
      }
      if (cleaned.length > 0) {
        localStorage.removeItem(LEGACY_FALLBACK_KEY);
        return cleaned;
      }
    }
  } catch {
    /* empty */
  }

  return [];
}

function writeBasketToDisk(items) {
  try {
    if (!items || items.length === 0) localStorage.removeItem(LOCAL_BASKET_KEY);
    else localStorage.setItem(LOCAL_BASKET_KEY, JSON.stringify(items));
  } catch {
    /* quota */
  }
}

function pickRfqProductId(product) {
  if (product == null || typeof product !== 'object') return '';
  const raw =
    product.id ??
    product._id ??
    product.product_id ??
    product.wc_id ??
    (product.wc_raw && typeof product.wc_raw === 'object' ? product.wc_raw.ID : undefined);
  if (raw != null && String(raw).trim()) return String(raw).trim();
  const sku =
    String(product.sku || (product.wc_raw && typeof product.wc_raw === 'object' ? product.wc_raw.SKU : '') || '')
      .trim();
  return sku || '';
}

function pickRfqProductName(product) {
  if (product == null || typeof product !== 'object') return '';
  const en = String(product.name || '').trim();
  if (en) return en;
  const tr = String(product.name_tr || '').trim();
  if (tr) return tr;
  const wc = product.wc_raw;
  const fromWc =
    wc && typeof wc === 'object'
      ? String(wc.Name || wc.name || wc.post_title || '').trim()
      : '';
  if (fromWc) return fromWc;
  const title = String(product.title || product.post_title || '').trim();
  if (title) return title;
  const sku =
    String(product.sku || (wc && typeof wc === 'object' ? wc.SKU : '') || '').trim();
  if (sku) return sku;
  const id = pickRfqProductId(product);
  return id ? `Product ${id}` : '';
}

export function RFQProvider({ children }) {
  const [items, setItems] = useState(() => readBasketFromDisk());
  /** Eski bileşenler sessionId bekleyebilir; depoda tutmuyoruz, yalnızca istemci benzersizi */
  const [sessionId] = useState(() => {
    try {
      let s = localStorage.getItem(RFQ_SESSION_LEGACY_KEY);
      if (s && /^[a-z0-9._-]{8}/i.test(s)) return s.trim();
      s = mintLineId().replace(/-/g, '').slice(0, 22);
      localStorage.setItem(RFQ_SESSION_LEGACY_KEY, s);
      return s;
    } catch {
      return 'local_rfqlocal';
    }
  });
  const [isOpen, setIsOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const persist = useCallback((next) => {
    writeBasketToDisk(next);
    setItems(next);
  }, []);

  const fetchBasket = useCallback(async () => {
    persist(readBasketFromDisk());
  }, [persist]);

  const addItem = useCallback(
    async (product) => {
      const product_id = pickRfqProductId(product);
      const product_name = pickRfqProductName(product);
      if (!product_id || !product_name) {
        return {
          ok: false,
          message: 'Missing product id or name. Try reloading the page.',
        };
      }

      const next = readBasketFromDisk();
      const ix = next.findIndex((x) => String(x.product_id) === String(product_id));
      if (ix >= 0) {
        next[ix] = {
          ...next[ix],
          quantity: Math.min(999999, (next[ix].quantity || 1) + 1),
        };
      } else {
        const line = sanitizeLine({
          id: mintLineId(),
          product_id,
          product_name,
          product_name_tr: (product.name_tr || '').trim(),
          quantity: 1,
          unit_type: 'carton',
          unit_qty: 1,
          unit_price: '',
          note: '',
          product_image: (product.image || '').trim(),
          created_at: new Date().toISOString(),
        });
        next.push(line);
      }
      persist(next);
      return { ok: true, savedOffline: false };
    },
    [persist],
  );

  const removeItem = useCallback(
    async (itemId) => {
      const idStr = String(itemId ?? '');
      const next = readBasketFromDisk().filter((x) => String(x.id) !== idStr);
      persist(next);
    },
    [persist],
  );

  const updateItemSpecs = useCallback(
    async (itemId, patch) => {
      const idStr = String(itemId ?? '');
      const next = readBasketFromDisk();
      const ix = next.findIndex((x) => String(x.id) === idStr);
      if (ix < 0) return;
      const current = next[ix];
      const unit_type = patch.unit_type != null ? parseUnitType(patch.unit_type) : current.unit_type;
      const unit_qty =
        patch.unit_qty != null && patch.unit_qty !== ''
          ? parseUnitQty(patch.unit_qty)
          : current.unit_qty;
      const unit_price = patch.unit_price != null ? sanitizeUnitPrice(patch.unit_price) : current.unit_price;
      next[ix] = {
        ...current,
        unit_type,
        unit_qty,
        unit_price,
        containers: unit_type === 'container' ? Math.min(9999, unit_qty) : current.containers,
      };
      persist(next);
    },
    [persist],
  );

  /**
   * @param {object} payload — form alanları (RFQ sayfasından)
   */
  const submitRFQ = useCallback(
    async (payload) => {
      let basketNow = readBasketFromDisk();

      if (basketNow.length === 0) {
        const formLines = buildFormOnlyBasketLines(payload);
        if (formLines.length) {
          basketNow = formLines;
        } else {
          return {
            ok: false,
            message: 'Your quote basket is empty. Add products first.',
            rateLimited: false,
          };
        }
      }

      const itemsPayload = basketNow.map((line) => ({
        id: line.id,
        product_id: line.product_id,
        product_name: line.product_name,
        product_name_tr: line.product_name_tr || '',
        quantity: line.quantity || 1,
        unit_type: line.unit_type || 'carton',
        unit_qty: line.unit_qty != null ? line.unit_qty : 1,
        unit_price: line.unit_price || '',
        containers: line.containers != null ? line.containers : 1,
        note: line.note || '',
        product_image: line.product_image || '',
        created_at: line.created_at,
      }));

      try {
        const { data } = await axios.post(
          `${API}/rfq/submit`,
          {
            session_id: sessionId,
            company_name: payload.company_name || '',
            contact_email: payload.contact_email || '',
            contact_phone: payload.contact_phone || '',
            contact_name: payload.contact_name || '',
            departure_port: payload.departure_port || 'Izmir, Turkiye',
            delivery_address: payload.delivery_address || '',
            destination_point: payload.destination_point || '',
            product_service: payload.product_service || '',
            delivery_location: payload.delivery_location || '',
            quantity_text: payload.quantity_text || '',
            supplier_types: payload.supplier_types || '',
            request_details: payload.request_details || '',
            items: itemsPayload,
          },
          { timeout: 25000 },
        );

        if (data?.success === false) {
          const msg = typeof data?.message === 'string' ? data.message : null;
          return { ok: false, message: msg, rateLimited: false };
        }

        persist([]);
        setSubmitted(true);

        try {
          const nid = mintLineId().replace(/-/g, '').slice(0, 22);
          localStorage.setItem(RFQ_SESSION_LEGACY_KEY, nid);
        } catch {
          /* ignore */
        }

        const emailWarn =
          typeof data?.email_send_error === 'string' && data.email_send_error.trim()
            ? data.email_send_error.trim()
            : data?.email_sent === false
              ? 'RFQ kaydedildi; sunucuda SMTP kullanıcı/şifre girilmezse bildirim e-postası gitmez.'
              : null;

        return { ok: true, emailWarning: emailWarn, rateLimited: false };
      } catch (e) {
        if (axios.isAxiosError(e) && e.response?.status === 429) {
          return { ok: false, message: null, rateLimited: true };
        }

        /** 502 vb. nginx/Node kopuklarında kullanıcıyı durdurmadan maile yönlendir */
        if (shouldMailtoFallbackSubmitError(e)) {
          const mailHref = buildRfqFallbackMailto(payload, basketNow);
          if (mailHref) {
            try {
              window.location.href = mailHref;
            } catch (openErr) {
              console.warn('mailto fallback failed', openErr);
            }
            persist([]);
            setSubmitted(true);
            try {
              const nid = mintLineId().replace(/-/g, '').slice(0, 22);
              localStorage.setItem(RFQ_SESSION_LEGACY_KEY, nid);
            } catch {
              /* ignore */
            }
            return {
              ok: true,
              emailWarning:
                'İstek süresi içinde sunucuya ulaşılmadı (502 veya zaman aşımı). E-posta uygulaması açılmış olmalı — Gönder ile tamamlayın.',
              rateLimited: false,
            };
          }
          return {
            ok: false,
            message:
              'Sunucuya ulaşılamadı (502). Yedek e-posta için projede REACT_APP_RFQ_MAILTO_EMAIL tanımlayın veya daha sonra tekrar deneyin.',
            rateLimited: false,
          };
        }

        const body = axios.isAxiosError(e) ? e.response?.data : null;
        const msg =
          typeof body?.message === 'string' && body.message.trim()
            ? body.message.trim()
            : typeof body?.detail === 'string'
              ? body.detail
              : e?.message || 'Request failed.';
        return {
          ok: false,
          message: String(msg).slice(0, 600),
          rateLimited: false,
        };
      }
    },
    [persist, sessionId],
  );

  return (
    <RFQ_CONTEXT.Provider
      value={{
        items,
        sessionId,
        isOpen,
        submitted,
        setIsOpen,
        setSubmitted,
        fetchBasket,
        addItem,
        removeItem,
        updateItemSpecs,
        submitRFQ,
      }}
    >
      {children}
    </RFQ_CONTEXT.Provider>
  );
}

export function useRFQ() {
  const c = useContext(RFQ_CONTEXT);
  if (!c) throw new Error('useRFQ must be used within RFQProvider');
  return c;
}
